import { IDropdownOption } from "@fluentui/react";
import { ISharepointDocumentLibrary } from "../../interfaces/ISharepointDocumentLibrary";
import { getSharePointContentTypeFields, getSharePointDocumentSetSettingsXml } from "../../services/SharePointRestServices";
import { parseString } from "xml2js";
import { SharepointFilters, SharePointFolderContentTypeIds } from "../../config/constants";
import { IDocumentSetSettings } from "../../interfaces/IDocumentSetSettings";
import { logger } from "../../../../utilities/logger";
import { IContentType } from "../../interfaces/IContentType";
import { SharePointInputTypes, SharePointMetadataBooleans, normalizeMultiValueFieldType } from "../../config/sharepointConstants";
import { dedupeChoices } from "./choiceOptionsHelper";

export class BaseSharepointHelper {
  static convertMetadataToFields = item => {
    let xmlData: any = {};
    parseString(item.SchemaXml, (err, result) => {
      if (result) {
        xmlData = result.Field.$;
      }
    });

    normalizeMultiValueFieldType(xmlData);

    if (xmlData.ShowInEditForm === SharePointMetadataBooleans.false
      || ((xmlData.Type === SharePointInputTypes.taxonomyFieldType || xmlData.Type === SharePointInputTypes.taxonomyFieldTypeMulti) && item.IsTermSetValid === false)) {
      return null;
    }
    else {
      if (item.Title) {
        xmlData.DisplayName = item.Title;
      }
      return {
        Choices: dedupeChoices(item.Choices),
        DefaultValue: item.DefaultValue,
        CurrentValue: item.DefaultValue,
        isValid: true,
        Id: item.Id,
        InternalName: item.InternalName,
        xml: xmlData
      };
    }
  };
}

export class SharepointContentTypeHelper extends BaseSharepointHelper {
  static mapDocumentContentTypes(result: any[]): IContentType[] {
    let contentTypes: IContentType[] = result
      .map(this.mapIContentType)
      .filter(contentType => !this.startsWith(contentType.id, SharePointFolderContentTypeIds.folder));

    return contentTypes;
  }

  static mapFolderOrDocumentSetContentTypes(result: any[], contentTypeId: string): IContentType[] {
    let contentTypes: IContentType[] = result
      .map(this.mapIContentType);

    if (!contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
      contentTypes = contentTypes.filter(contentType => this.startsWith(contentType.id, SharePointFolderContentTypeIds.folder) && !this.startsWith(contentType.id, SharePointFolderContentTypeIds.documentSet));
    }
    else {
      contentTypes = contentTypes.filter(contentType => this.startsWith(contentType.id, SharePointFolderContentTypeIds.documentSet));
    }

    return contentTypes;
  }

  static mapContentTypesToDropdownOptions(result: any[]): IDropdownOption[] {
    let contentTypes: IDropdownOption[] = result
      .map(this.mapKeyText)
      .filter((item: { key: any }) => !this.startsWith(item.key, SharePointFolderContentTypeIds.folder));

    return contentTypes;
  }

  static mapDocumentSetContentTypesToDropdownOptions(result: any[]): IDropdownOption[] {
    let contentTypes: IDropdownOption[] = result
      .map(this.mapKeyText)
      .filter((item: { key: any }) => this.startsWith(item.key, SharePointFolderContentTypeIds.documentSet));

    return contentTypes;
  }

  static mapFolderContentTypesToDropdownOptions(result: any[]): IDropdownOption[] {
    let contentTypes: IDropdownOption[] = result
      .map(this.mapKeyText)
      .filter((item: { key: any }) => this.startsWith(item.key, SharePointFolderContentTypeIds.folder));
    return contentTypes;
  }

  private static startsWith = (full: string | any[], word: any) => {
    return full?.lastIndexOf(word, 0) === 0;
  };

  private static mapKeyText = (item: { Id: { StringValue: any }; Name: any }) => ({
    key: item.Id.StringValue,
    text: item.Name
  });

  private static mapIContentType = (contentType): IContentType => ({
    id: contentType.Id.StringValue,
    name: contentType.Name,
    parent: {
      id: contentType.Parent?.Id.StringValue
    }
  });

  static async getFields(accessToken: string, contentTypeId: string | number, documentLib: ISharepointDocumentLibrary) {
    let result = await getSharePointContentTypeFields(
      accessToken,
      documentLib.webUrl,
      documentLib.sharePointListId,
      contentTypeId as string,
      SharepointFilters.metadata.save
    );
    return result.map(this.convertMetadataToFields).filter(field => field);
  }

  static async getDocumentSetSettings(accessToken: string, webUrl: string, sharePointListId: string, contentTypeId: string): Promise<IDocumentSetSettings> {
    let allowedContentTypeIds: string[] = [];
    let sharedFieldIds: string[] = [];
    let viewId: string;

    try {
      const documentSetXml = await getSharePointDocumentSetSettingsXml(accessToken, webUrl, sharePointListId, contentTypeId);
      if (documentSetXml) {
        let convertedXmlData: any;
        parseString(documentSetXml.SchemaXml, (err, xmlResult) => {
          if (xmlResult) {
            convertedXmlData = xmlResult;
          }
        });

        if (convertedXmlData.ContentType.XmlDocuments
          && Array.isArray(convertedXmlData.ContentType.XmlDocuments)
          && convertedXmlData.ContentType.XmlDocuments.length > 0) {

          let xmlDocSets = convertedXmlData.ContentType.XmlDocuments[0].XmlDocument;
          if (Array.isArray(xmlDocSets)) {
            xmlDocSets.forEach((xmlDocSet) => {
              if (Array.isArray(xmlDocSet["act:AllowedContentTypes"])) {
                xmlDocSet["act:AllowedContentTypes"].forEach(allowedContentTypes => {
                  if (Array.isArray(allowedContentTypes.AllowedContentType)) {
                    allowedContentTypeIds = allowedContentTypeIds.concat(
                      allowedContentTypes.AllowedContentType
                        .map(allowedContentType => allowedContentType.$?.id)
                        .filter(allowedContentType => allowedContentType)
                    )
                  }
                })
              }
              else if (Array.isArray(xmlDocSet["sf:SharedFields"])) {
                xmlDocSet["sf:SharedFields"].forEach(sharedFields => {
                  let sharedFieldArray: any[];
                  if (Array.isArray(sharedFields.SharedField)) {
                    sharedFieldArray = sharedFields.SharedField;
                  }
                  else if (Array.isArray(sharedFields["sf:SharedField"])) {
                    sharedFieldArray = sharedFields["sf:SharedField"];
                  }

                  if (Array.isArray(sharedFieldArray)) {
                    sharedFieldIds = sharedFieldIds.concat(
                      sharedFieldArray
                        .map(sharedField => sharedField.$?.id)
                        .filter(sharedField => sharedField)
                    );
                  }
                })
              }
              else if (Array.isArray(xmlDocSet["wpv:WelcomePageView"])) {
                const view = xmlDocSet["wpv:WelcomePageView"];
                if (view.length > 0) {
                  viewId = view[0].$?.ViewId;
                }
              }
            });
          }
        }
      }
    } catch (error) {
      logger(error, "sharepointContentTypeHelper.ts (getDocumentSetSettings)");
    }

    return { allowedContentTypeIds: allowedContentTypeIds, sharedFieldIds: sharedFieldIds, viewId: viewId || null };
  }
}